import { useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import InsightsIcon from "@mui/icons-material/Insights";
import { useTranslation } from "react-i18next";
import colors from "../../Constants/colors";
import { useUsage } from "../../Query/useUsage";
import { UsageGroupBy, UsageRow, UsageSource } from "../../Models/usage";
import {
  buildPeriod,
  formatTokens,
  formatUsd,
} from "../../Helpers/usageWindow";
import { ProfileIconBox } from "../Profile/ProfileSection";
import UsagePeriodSelector from "./UsagePeriodSelector";

type Breakdown = "model" | "entity" | "certification" | "question_list";

type SourceFilter =
  | "all"
  | "evaluation"
  | "chat"
  | "voices"
  | "document_crafting";

interface BreakdownConfig {
  idField: keyof UsageRow;
  nameField: keyof UsageRow;
  // Shown when the row has no id at all (usage not tied to this dimension).
  emptyKey: string;
  // Shown when the id survives but the referenced record is gone.
  fallbackKey: string;
}

const BREAKDOWNS: Record<Breakdown, BreakdownConfig> = {
  model: {
    idField: "model_id",
    nameField: "model",
    emptyKey: "notApplicable",
    fallbackKey: "notApplicable",
  },
  entity: {
    idField: "entity_id",
    nameField: "entity",
    emptyKey: "notApplicable",
    fallbackKey: "deletedEntity",
  },
  certification: {
    idField: "certification_category_id",
    nameField: "certification",
    emptyKey: "notApplicable",
    fallbackKey: "deletedCertification",
  },
  question_list: {
    idField: "question_list_id",
    nameField: "question_list",
    emptyKey: "notApplicable",
    fallbackKey: "deletedQuestionList",
  },
};

const SOURCE_VALUES: Record<SourceFilter, UsageSource | undefined> = {
  all: undefined,
  evaluation: UsageSource.EVALUATION,
  chat: UsageSource.CHAT,
  voices: UsageSource.COMO_VOICES,
  document_crafting: UsageSource.DOCUMENT_CRAFTING,
};

interface Props {
  userId?: string | number;
  workspaceId?: number;
  email?: string;
  // When rangeControlled is set, the widget follows the parent's from/to
  // (which may be empty for "all time") and hides its own selector.
  rangeControlled?: boolean;
  from?: string;
  to?: string;
}

const StatTile = ({ label, value }: { label: string; value: string }) => (
  <Paper
    elevation={0}
    sx={{
      flex: 1,
      p: 1.5,
      borderRadius: 2,
      border: `1px solid ${colors.gray200}`,
      background: colors.gray50,
    }}
  >
    <Typography variant="caption" sx={{ color: colors.gray500 }}>
      {label}
    </Typography>
    <Typography
      sx={{ fontWeight: 700, fontSize: "1.1rem", color: colors.black }}
    >
      {value}
    </Typography>
  </Paper>
);

const CategoryTile = ({
  label,
  tokens,
  cost,
}: {
  label: string;
  tokens: number;
  cost: number;
}) => (
  <Paper
    elevation={0}
    sx={{
      flex: "1 1 120px",
      minWidth: 110,
      p: 1.25,
      borderRadius: 2,
      border: `1px solid ${colors.gray200}`,
      background: colors.gray50,
    }}
  >
    <Typography variant="caption" sx={{ color: colors.gray500 }}>
      {label}
    </Typography>
    <Typography
      sx={{ fontWeight: 700, fontSize: "0.95rem", color: colors.black }}
    >
      {formatUsd(cost)}
    </Typography>
    <Typography sx={{ fontSize: "0.72rem", color: colors.gray500 }}>
      {formatTokens(tokens)}
    </Typography>
  </Paper>
);

const UsageWidget = ({
  userId,
  workspaceId,
  email,
  rangeControlled,
  from,
  to,
}: Props) => {
  const { t } = useTranslation("usage");
  const isControlled =
    rangeControlled === true || from !== undefined || to !== undefined;

  const [breakdown, setBreakdown] = useState<Breakdown>("model");
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>("all");
  const [range, setRange] = useState<{ from?: string; to?: string }>(() =>
    buildPeriod("thisMonth"),
  );

  const window = isControlled ? { from, to } : range;

  const numericUserId =
    userId !== undefined && userId !== null && `${userId}`.length > 0
      ? Number(userId)
      : undefined;

  const enabled =
    numericUserId !== undefined || workspaceId !== undefined || !!email;

  const source = SOURCE_VALUES[sourceFilter];

  const params = useMemo(
    () => ({
      group_by: breakdown as UsageGroupBy,
      user_id: numericUserId,
      workspace_id: workspaceId,
      email,
      source,
      from: window.from,
      to: window.to,
    }),
    [
      breakdown,
      numericUserId,
      workspaceId,
      email,
      source,
      window.from,
      window.to,
    ],
  );

  const { data, isLoading } = useUsage(params, enabled);

  const rows: UsageRow[] = data ?? [];

  const totals = useMemo(
    () =>
      rows.reduce(
        (acc, r) => ({
          tokens: acc.tokens + (r.tokens_total ?? 0),
          cost: acc.cost + (r.cost_total ?? 0),
          inputTokens: acc.inputTokens + (r.input_tokens ?? 0),
          inputCost: acc.inputCost + (r.input_cost ?? 0),
          cachedTokens: acc.cachedTokens + (r.cached_input_tokens ?? 0),
          cachedCost: acc.cachedCost + (r.cached_input_cost ?? 0),
          reasoningTokens: acc.reasoningTokens + (r.reasoning_tokens ?? 0),
          reasoningCost: acc.reasoningCost + (r.reasoning_cost ?? 0),
          outputTokens: acc.outputTokens + (r.output_tokens ?? 0),
          outputCost: acc.outputCost + (r.output_cost ?? 0),
          toolCalls: acc.toolCalls + (r.tool_calls ?? 0),
          iterations: acc.iterations + (r.iterations ?? 0),
        }),
        {
          tokens: 0,
          cost: 0,
          inputTokens: 0,
          inputCost: 0,
          cachedTokens: 0,
          cachedCost: 0,
          reasoningTokens: 0,
          reasoningCost: 0,
          outputTokens: 0,
          outputCost: 0,
          toolCalls: 0,
          iterations: 0,
        },
      ),
    [rows],
  );

  // Only the per-model rows carry the cost split per token type, and tool
  // calls / iterations only exist for chat usage.
  const isModel = breakdown === "model";
  const showChatColumns = sourceFilter === "chat";
  const config = BREAKDOWNS[breakdown];

  const rowId = (row: UsageRow): number | null | undefined =>
    row[config.idField] as number | null | undefined;

  const rowLabel = (row: UsageRow): string => {
    const name = row[config.nameField] as string;
    if (name) return name;
    const id = rowId(row);
    return id === null || id === undefined
      ? t(config.emptyKey)
      : t(config.fallbackKey);
  };

  const columnCount = (isModel ? 6 : 7) + (showChatColumns ? 2 : 0);

  const headerCellSx = {
    fontWeight: 600,
    color: colors.gray700,
    backgroundColor: colors.gray50,
    borderBottom: `1px solid ${colors.gray200}`,
    fontSize: "0.75rem",
    whiteSpace: "nowrap" as const,
  };

  const bodyCellSx = {
    color: colors.black,
    fontSize: "0.8rem",
    borderBottom: `1px solid ${colors.gray100}`,
  };

  const toggleSx = {
    "& .MuiToggleButton-root.Mui-selected": {
      backgroundColor: colors.accent,
      color: colors.black,
      "&:hover": { backgroundColor: colors.accent },
    },
  };

  const TokenCostCell = ({
    tokens,
    cost,
  }: {
    tokens: number | undefined;
    cost: number | undefined;
  }) => (
    <TableCell sx={bodyCellSx} align="right">
      <Typography sx={{ fontSize: "0.8rem", color: colors.black }}>
        {formatUsd(cost)}
      </Typography>
      <Typography sx={{ fontSize: "0.7rem", color: colors.gray500 }}>
        {formatTokens(tokens)}
      </Typography>
    </TableCell>
  );

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 2.5 },
        borderRadius: "16px",
        border: `1px solid ${colors.gray200}`,
        background: colors.white,
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={1.5}
        sx={{ mb: 2 }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <ProfileIconBox>
            <InsightsIcon />
          </ProfileIconBox>
          <Typography
            sx={{
              fontSize: 16,
              fontWeight: 700,
              lineHeight: "22px",
              color: colors.primary_dark,
            }}
          >
            {t("widgetTitle")}
          </Typography>
        </Stack>

        {!isControlled && <UsagePeriodSelector onChange={setRange} />}
      </Stack>

      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1.5}
        alignItems={{ xs: "stretch", md: "center" }}
        justifyContent="space-between"
        flexWrap="wrap"
        useFlexGap
        sx={{ mb: 1.5 }}
      >
        <ToggleButtonGroup
          size="small"
          exclusive
          value={breakdown}
          onChange={(_, value) => value && setBreakdown(value)}
          sx={{ flexWrap: "wrap", ...toggleSx }}
        >
          {(Object.keys(BREAKDOWNS) as Breakdown[]).map((value) => (
            <ToggleButton key={value} value={value}>
              {t(value)}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <ToggleButtonGroup
          size="small"
          exclusive
          value={sourceFilter}
          onChange={(_, value) => value && setSourceFilter(value)}
          sx={{ flexWrap: "wrap", ...toggleSx }}
        >
          <ToggleButton value="all">{t("allSources")}</ToggleButton>
          <ToggleButton value="evaluation">{t("evaluations")}</ToggleButton>
          <ToggleButton value="chat">{t("chats")}</ToggleButton>
          <ToggleButton value="voices">{t("voices")}</ToggleButton>
          <ToggleButton value="document_crafting">
            {t("documentCrafting")}
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      <Stack direction="row" spacing={1.5} sx={{ mb: 1.5 }}>
        <StatTile label={t("totalCost")} value={formatUsd(totals.cost)} />
        <StatTile
          label={t("totalTokens")}
          value={formatTokens(totals.tokens)}
        />
      </Stack>

      {isModel && (
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 1.5 }}>
          <CategoryTile
            label={t("inputTokens")}
            tokens={totals.inputTokens}
            cost={totals.inputCost}
          />
          <CategoryTile
            label={t("cachedTokens")}
            tokens={totals.cachedTokens}
            cost={totals.cachedCost}
          />
          <CategoryTile
            label={t("reasoningTokens")}
            tokens={totals.reasoningTokens}
            cost={totals.reasoningCost}
          />
          <CategoryTile
            label={t("outputTokens")}
            tokens={totals.outputTokens}
            cost={totals.outputCost}
          />
        </Box>
      )}

      <TableContainer
        sx={{
          border: `1px solid ${colors.gray200}`,
          borderRadius: 2,
          overflow: "auto",
          position: "relative",
          minHeight: 120,
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={headerCellSx}>{t(breakdown)}</TableCell>
              <TableCell sx={headerCellSx} align="right">
                {t("inputTokens")}
              </TableCell>
              <TableCell sx={headerCellSx} align="right">
                {t("cachedTokens")}
              </TableCell>
              <TableCell sx={headerCellSx} align="right">
                {t("reasoningTokens")}
              </TableCell>
              <TableCell sx={headerCellSx} align="right">
                {t("outputTokens")}
              </TableCell>
              {showChatColumns && (
                <>
                  <TableCell sx={headerCellSx} align="right">
                    {t("toolCalls")}
                  </TableCell>
                  <TableCell sx={headerCellSx} align="right">
                    {t("iterations")}
                  </TableCell>
                </>
              )}
              {isModel ? (
                <TableCell sx={headerCellSx} align="right">
                  {t("total")}
                </TableCell>
              ) : (
                <>
                  <TableCell sx={headerCellSx} align="right">
                    {t("totalTokens")}
                  </TableCell>
                  <TableCell sx={headerCellSx} align="right">
                    {t("cost")}
                  </TableCell>
                </>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "rgba(255,255,255,0.6)",
                  zIndex: 10,
                }}
              >
                <CircularProgress size={28} sx={{ color: colors.accent }} />
              </Box>
            )}
            {rows.length > 0
              ? rows.map((row, index) => (
                  <TableRow
                    key={`${breakdown}-${rowId(row) ?? "none"}-${index}`}
                  >
                    <TableCell sx={bodyCellSx}>{rowLabel(row)}</TableCell>
                    {isModel ? (
                      <>
                        <TokenCostCell
                          tokens={row.input_tokens}
                          cost={row.input_cost}
                        />
                        <TokenCostCell
                          tokens={row.cached_input_tokens}
                          cost={row.cached_input_cost}
                        />
                        <TokenCostCell
                          tokens={row.reasoning_tokens}
                          cost={row.reasoning_cost}
                        />
                        <TokenCostCell
                          tokens={row.output_tokens}
                          cost={row.output_cost}
                        />
                      </>
                    ) : (
                      <>
                        <TableCell sx={bodyCellSx} align="right">
                          {formatTokens(row.input_tokens)}
                        </TableCell>
                        <TableCell sx={bodyCellSx} align="right">
                          {formatTokens(row.cached_input_tokens)}
                        </TableCell>
                        <TableCell sx={bodyCellSx} align="right">
                          {formatTokens(row.reasoning_tokens)}
                        </TableCell>
                        <TableCell sx={bodyCellSx} align="right">
                          {formatTokens(row.output_tokens)}
                        </TableCell>
                      </>
                    )}
                    {showChatColumns && (
                      <>
                        <TableCell sx={bodyCellSx} align="right">
                          {formatTokens(row.tool_calls)}
                        </TableCell>
                        <TableCell sx={bodyCellSx} align="right">
                          {formatTokens(row.iterations)}
                        </TableCell>
                      </>
                    )}
                    {isModel ? (
                      <TokenCostCell
                        tokens={row.tokens_total}
                        cost={row.cost_total}
                      />
                    ) : (
                      <>
                        <TableCell sx={bodyCellSx} align="right">
                          {formatTokens(row.tokens_total)}
                        </TableCell>
                        <TableCell sx={bodyCellSx} align="right">
                          {formatUsd(row.cost_total)}
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                ))
              : !isLoading && (
                  <TableRow>
                    <TableCell
                      colSpan={columnCount}
                      align="center"
                      sx={{
                        color: colors.gray500,
                        py: 4,
                        backgroundColor: colors.gray50,
                        fontSize: "0.8rem",
                      }}
                    >
                      {t("noData")}
                    </TableCell>
                  </TableRow>
                )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default UsageWidget;
